/**
 * Auth Provider Component
 *
 * Wraps the app and handles:
 * - Local JWT auth initialization
 * - Loading state during auth check
 * - Protected route logic
 */

import { useEffect, useCallback, ReactNode } from 'react';
import { useAuthStore } from './authStore';
import { LoginPage } from '../components/auth/LoginPage';
import { useIdleTimeout } from './useIdleTimeout';
import { Loader2 } from 'lucide-react';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { user, isInitialized, isLoading, initialize, logout } = useAuthStore();

  // Initialize auth on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Log the user out after 30 minutes of inactivity (protects an
  // unattended screen from unauthorised access).
  const handleIdleTimeout = useCallback(() => {
    void logout();
  }, [logout]);
  useIdleTimeout(!!user, handleIdleTimeout);

  // Show loading spinner while checking auth
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-500 animate-spin mx-auto" />
          <p className="text-gray-400 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!user) {
    return <LoginPage />;
  }

  // Render children (protected app)
  return <>{children}</>;
}

/**
 * User Menu Component
 *
 * Shows current user info and logout button.
 * Use this in your header/navbar.
 */
export function UserMenu() {
  const { user, profile, logout, isLoading } = useAuthStore();

  if (!user) return null;

  const displayName = profile?.display_name || user.email?.split('@')[0] || 'User';
  const avatarUrl = profile?.avatar_url;
  const initials = displayName.charAt(0).toUpperCase();

  return (
    <div className="flex items-center gap-3">
      {/* Avatar */}
      <div className="relative">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={displayName}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-sm font-medium">
            {initials}
          </div>
        )}
      </div>

      {/* User Info */}
      <div className="hidden sm:block">
        <p className="text-sm font-medium text-white">{displayName}</p>
        <p className="text-xs text-gray-400">{profile?.role || 'user'}</p>
      </div>

      {/* Logout Button */}
      <button
        onClick={() => logout()}
        disabled={isLoading}
        className="ml-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
      >
        Logout
      </button>
    </div>
  );
}
